import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Image, TrendingUp, Share2, Star } from "lucide-react";

const APP_VERSION = "2.1.0"; // Update this with each release

const updates = [
  {
    icon: Image,
    title: "Smart Image Suggestions",
    description: "AI now includes relevant images with recipes, travel tips, and more!",
    color: "from-blue-500 to-cyan-500"
  },
  {
    icon: TrendingUp,
    title: "Popular Prompts",
    description: "New quick-start prompts on the home screen to inspire your conversations",
    color: "from-purple-500 to-pink-500"
  },
  {
    icon: Share2,
    title: "Easy Sharing",
    description: "Share AI responses with friends directly from the chat",
    color: "from-green-500 to-emerald-500"
  },
  {
    icon: Star,
    title: "Smarter AI",
    description: "Upgraded to Gemini 3 Flash for faster, more accurate responses",
    color: "from-yellow-500 to-orange-500"
  }
];

const WhatsNewModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if user has seen this version's updates
    const seenVersion = localStorage.getItem("nexi_whats_new_version");
    
    // Show modal if this is a new version they haven't seen
    if (seenVersion !== APP_VERSION) {
      // Small delay to not overwhelm user on app load
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    // Mark this version as seen
    localStorage.setItem("nexi_whats_new_version", APP_VERSION);
    setIsOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-card border border-white/10 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-primary/20 via-secondary/20 to-accent/20 p-6 text-center">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="whats-new-close"
              >
                <X size={18} className="text-white/70" />
              </button>
              
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary shadow-lg shadow-primary/50 mb-4"
              >
                <Sparkles size={32} className="text-white" />
              </motion.div>
              
              <h2 className="text-2xl font-bold text-white mb-1">What's New!</h2>
              <p className="text-white/60 text-sm">Version {APP_VERSION}</p>
            </div>

            {/* Updates List */}
            <div className="p-4 space-y-3 max-h-[50vh] overflow-y-auto">
              {updates.map((update, index) => {
                const Icon = update.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index + 0.3 }}
                    className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10"
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${update.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium text-sm">{update.title}</h3>
                      <p className="text-white/50 text-xs mt-0.5">{update.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleClose}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-primary to-secondary text-white font-medium shadow-lg shadow-primary/30"
                data-testid="whats-new-got-it"
              >
                Got it, let's go!
              </motion.button>
              
              <p className="text-center text-white/30 text-xs mt-3">
                Enjoying Nexi? Leave us a review!
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WhatsNewModal;
